/**
 * Dashboard Unifié - Vue consolidée des tâches en cours
 * Affiche toutes les adhésions FTPE, souscriptions RCDS, avenants avec filtres
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Shield, FileEdit, FilePlus, Calculator, Search, RefreshCw, ChevronDown, Calendar, MapPin, Clock, ArrowRight, MoreHorizontal, Download, Trash2, Mail, Eye, Edit, AlertTriangle, TrendingUp, Loader2, X, SlidersHorizontal } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DatePickerFr } from '../components/ui/DatePickerFr';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { getToken } from '../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Icons mapping
const TASK_ICONS = {
  Users: Users,
  Shield: Shield,
  FileEdit: FileEdit,
  FilePlus: FilePlus,
  Calculator: Calculator
};

// Color classes mapping
const COLOR_CLASSES = {
  gray: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  green: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  red: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' }
};

const DashboardUnified = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedTerritory, setSelectedTerritory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  const token = getToken();

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedType) params.append('task_type', selectedType);
      if (selectedTerritory) params.append('territory', selectedTerritory);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API}/dashboard-unified/tasks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTasks(response.data.tasks || []);
      setTotalCount(response.data.total_count || 0);
    } catch {
      toast.error('Erreur lors du chargement des tâches');
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery, selectedStatus, selectedType, selectedTerritory, dateFrom, dateTo]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard-unified/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch {
    }
  }, [token]);

  // Fetch available filters
  const fetchFilters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard-unified/filters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilters(response.data);
    } catch {
    }
  }, [token]);

  useEffect(() => {
    fetchFilters();
    fetchStats();
  }, [fetchFilters, fetchStats]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchTasks();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [fetchTasks]);

  // Handle task actions
  const handleTaskAction = (task, action) => {
    switch (action) {
      case 'view':
      case 'resume':
        if (task.type === 'ftpe_adhesion' || task.type === 'ftpe_draft') {
          if (task.type === 'ftpe_draft') {
            navigate(`/adhesion-ftpe-workflow?draft_id=${task.id}`);
          } else {
            navigate(`/adhesion-ftpe-workflow?adhesion_id=${task.id}&step=payment`);
          }
        } else if (task.type === 'souscription_rcds' || task.type === 'simulation') {
          navigate(`/simulateur?id=${task.id}`);
        } else if (task.type === 'avenant') {
          navigate(`/avenants/${task.id}`);
        }
        break;
      case 'edit':
        if (task.type === 'souscription_rcds' || task.type === 'simulation') {
          navigate(`/simulateur?id=${task.id}&edit=true`);
        }
        break;
      case 'download':
        toast.success('Téléchargement en cours...');
        break;
      case 'delete':
        toast.error('Fonctionnalité de suppression à confirmer');
        break;
      case 'send_reminder':
        toast.success('Rappel envoyé');
        break;
      default:
        break;
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('');
    setSelectedType('');
    setSelectedTerritory('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = searchQuery || selectedStatus || selectedType || selectedTerritory || dateFrom || dateTo;

  // Render task icon
  const TaskIcon = ({ type, color }) => {
    const Icon = TASK_ICONS[type] || FileEdit;
    const colorClass = COLOR_CLASSES[color] || COLOR_CLASSES.gray;
    return (
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colorClass.bg)}>
        <Icon className={cn("w-5 h-5", colorClass.text)} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Dashboard Unifié</h1>
            <p className="text-slate-600 mt-1">Vue consolidée de toutes vos tâches en cours</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => { fetchTasks(); fetchStats(); }}
              disabled={loading}
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">FTPE</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.ftpe?.total || 0}</p>
                    <p className="text-xs text-amber-600">{stats.ftpe?.pending || 0} en cours</p>
                  </div>
                  <Users className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Souscriptions</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.souscriptions?.total || 0}</p>
                    <p className="text-xs text-blue-600">{stats.souscriptions?.pending || 0} en attente</p>
                  </div>
                  <Shield className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Avenants</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.avenants?.total || 0}</p>
                  </div>
                  <FilePlus className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-gray-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Brouillons</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.drafts?.total || 0}</p>
                    <p className="text-xs text-orange-600">{stats.drafts?.expiring_soon || 0} expirent bientôt</p>
                  </div>
                  <FileEdit className="w-8 h-8 text-gray-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Activité 30j</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.recent_activity || 0}</p>
                    <p className="text-xs text-green-600">nouvelles tâches</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters Section */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par nom, email, référence..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Quick Filters */}
              <div className="flex flex-wrap gap-2">
                {/* Type Filter */}
                <Select value={selectedType} onValueChange={(val) => setSelectedType(val === 'all' ? '' : val)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Type de tâche" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {filters?.task_types?.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Status Filter */}
                <Select value={selectedStatus} onValueChange={(val) => setSelectedStatus(val === 'all' ? '' : val)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    {filters?.statuses?.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Territory Filter */}
                <Select value={selectedTerritory} onValueChange={(val) => setSelectedTerritory(val === 'all' ? '' : val)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Territoire" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les territoires</SelectItem>
                    {filters?.territories?.map((territory) => (
                      <SelectItem key={territory} value={territory}>
                        {territory}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* More Filters Toggle */}
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className={cn(showFilters && "bg-slate-100")}
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Plus de filtres
                  <ChevronDown className={cn("w-4 h-4 ml-2 transition-transform", showFilters && "rotate-180")} />
                </Button>
                
                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button variant="ghost" onClick={clearFilters} className="text-red-600">
                    <X className="w-4 h-4 mr-1" />
                    Effacer
                  </Button>
                )}
              </div>
            </div>
            
            {/* Extended Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-600">Du</span>
                  <DatePickerFr
                    value={dateFrom}
                    onChange={setDateFrom}
                    className="w-40"
                  />
                  <span className="text-sm text-slate-600">au</span>
                  <DatePickerFr
                    value={dateTo}
                    onChange={setDateTo}
                    className="w-40"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            <span className="font-semibold">{totalCount}</span> tâche(s) trouvée(s)
          </p>
        </div>

        {/* Tasks List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                <span className="ml-3 text-slate-500">Chargement des tâches...</span>
              </div>
            ) : tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700">Aucune tâche trouvée</h3>
                <p className="text-slate-500 mt-1 max-w-md">
                  {hasActiveFilters 
                    ? "Essayez de modifier vos filtres pour voir plus de résultats."
                    : "Vous n'avez pas encore de tâches en cours."}
                </p>
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters} className="mt-4">
                    Effacer les filtres
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {tasks.map((task) => {
                  const colorClass = COLOR_CLASSES[task.status_color] || COLOR_CLASSES.gray;
                  const typeColorClass = COLOR_CLASSES[task.type_color] || COLOR_CLASSES.gray;
                  
                  return (
                    <div
                      key={`${task.type}-${task.id}`}
                      className={cn(
                        "p-4 hover:bg-slate-50 transition-colors cursor-pointer",
                        task.priority === 'high' && "bg-amber-50/50"
                      )}
                      onClick={() => handleTaskAction(task, 'view')}
                    >
                      <div className="flex items-center gap-4">
                        {/* Icon */}
                        <TaskIcon type={task.type_icon} color={task.type_color} />
                        
                        {/* Main Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900 truncate">
                              {task.title}
                            </h3>
                            {task.priority === 'high' && (
                              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="truncate">{task.subtitle}</span>
                            {task.reference && (
                              <>
                                <span className="text-slate-300">•</span>
                                <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
                                  {task.reference}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Badges & Info */}
                        <div className="hidden md:flex items-center gap-3">
                          {/* Type Badge */}
                          <Badge variant="outline" className={cn("text-xs", typeColorClass.text, typeColorClass.border)}>
                            {task.type_label}
                          </Badge>
                          
                          {/* Status Badge */}
                          <Badge className={cn(colorClass.bg, colorClass.text, "border-0")}>
                            {task.status_label}
                          </Badge>
                          
                          {/* Territory */}
                          {task.territory && (
                            <div className="flex items-center gap-1 text-xs text-slate-500">
                              <MapPin className="w-3 h-3" />
                              <span>{task.territory}</span>
                            </div>
                          )}
                          
                          {/* Amount */}
                          {task.amount > 0 && (
                            <span className="font-semibold text-slate-700">
                              {task.amount.toLocaleString('fr-FR')} €
                            </span>
                          )}
                          
                          {/* Days Remaining (for drafts) */}
                          {task.days_remaining !== undefined && (
                            <div className={cn(
                              "flex items-center gap-1 text-xs font-medium",
                              task.days_remaining > 10 ? "text-green-600" :
                              task.days_remaining > 3 ? "text-orange-600" : "text-red-600"
                            )}>
                              <Clock className="w-3 h-3" />
                              {task.days_remaining}j
                            </div>
                          )}
                        </div>
                        
                        {/* Date */}
                        <div className="hidden lg:block text-right text-xs text-slate-500 min-w-[100px]">
                          {task.created_at && (
                            <div>
                              {new Date(task.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {task.actions?.includes('view') && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskAction(task, 'view'); }}>
                                <Eye className="w-4 h-4 mr-2" />
                                Voir détails
                              </DropdownMenuItem>
                            )}
                            {task.actions?.includes('resume') && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskAction(task, 'resume'); }}>
                                <ArrowRight className="w-4 h-4 mr-2" />
                                Reprendre
                              </DropdownMenuItem>
                            )}
                            {task.actions?.includes('edit') && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskAction(task, 'edit'); }}>
                                <Edit className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                            )}
                            {task.actions?.includes('download') && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskAction(task, 'download'); }}>
                                <Download className="w-4 h-4 mr-2" />
                                Télécharger
                              </DropdownMenuItem>
                            )}
                            {task.actions?.includes('send_reminder') && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskAction(task, 'send_reminder'); }}>
                                <Mail className="w-4 h-4 mr-2" />
                                Envoyer rappel
                              </DropdownMenuItem>
                            )}
                            {task.actions?.includes('delete') && (
                              <DropdownMenuItem 
                                onClick={(e) => { e.stopPropagation(); handleTaskAction(task, 'delete'); }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      {/* Mobile Info */}
                      <div className="md:hidden mt-3 flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={cn("text-xs", typeColorClass.text, typeColorClass.border)}>
                          {task.type_label}
                        </Badge>
                        <Badge className={cn(colorClass.bg, colorClass.text, "border-0 text-xs")}>
                          {task.status_label}
                        </Badge>
                        {task.amount > 0 && (
                          <span className="text-sm font-semibold text-slate-700">
                            {task.amount.toLocaleString('fr-FR')} €
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardUnified;
